import './MeseroPlatilloSelector.css';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { bindTouchAxisScroll } from './meseroTouchAxisScroll';

const CATEGORY_ORDER = ['Postres', 'Botanas', 'Comida', 'Bebidas', 'Waffles'];

const platilloKey = (platillo) => platillo.NombrePlatillo;

const MeseroPlatilloSelector = ({ addPlatilloToOrder, platillos, floating = false, inHead = false }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [cart, setCart] = useState({});
    const modalBodyRef = useRef(null);
    const notify = (message) => toast(message);

    useEffect(() => {
        if (!modalOpen) {
            return undefined;
        }
        const bodyEl = modalBodyRef.current;
        if (!bodyEl) {
            return undefined;
        }
        return bindTouchAxisScroll(bodyEl, { axis: 'y' });
    }, [modalOpen, platillos.length]);

    const platillosByCategory = useMemo(() => {
        const known = new Set(CATEGORY_ORDER);
        const extras = [...new Set(
            platillos
                .map((p) => p.Categoria)
                .filter((c) => c && !known.has(c))
        )];
        const orderedCategories = [...CATEGORY_ORDER, ...extras];

        return orderedCategories
            .map((name) => ({
                name,
                items: platillos.filter((p) => p.Categoria === name),
            }))
            .filter((section) => section.items.length > 0);
    }, [platillos]);

    const totalItems = useMemo(
        () => Object.values(cart).reduce((sum, entry) => sum + entry.qty, 0),
        [cart]
    );

    const closeModal = () => {
        setModalOpen(false);
        setCart({});
    };

    const openModal = () => {
        setCart({});
        setModalOpen(true);
    };

    const setCartQty = (platillo, qty) => {
        const key = platilloKey(platillo);
        setCart((prev) => {
            const next = { ...prev };
            if (qty <= 0) {
                delete next[key];
            } else {
                next[key] = { platillo, qty };
            }
            return next;
        });
    };

    const handlePlatilloTap = (platillo) => {
        if (platillo.Disponibilidad === 0) {
            notify('Platillo no disponible');
            return;
        }
        const key = platilloKey(platillo);
        const current = cart[key]?.qty || 0;
        setCartQty(platillo, current + 1);
    };

    const adjustQty = (platillo, delta) => {
        if (platillo.Disponibilidad === 0) return;
        const key = platilloKey(platillo);
        const current = cart[key]?.qty || 0;
        setCartQty(platillo, current + delta);
    };

    const handleConfirmAdd = () => {
        const entries = Object.values(cart)
            .filter((entry) => entry.qty > 0)
            .map(({ platillo, qty }) => ({ platillo, quantity: qty }));
        if (!entries.length) {
            notify('Selecciona al menos un platillo');
            return;
        }
        addPlatilloToOrder(entries);
        closeModal();
    };

    return (
        <div className={`mesero-platillo-selector${floating ? ' mesero-platillo-selector--floating' : ''}${inHead ? ' mesero-platillo-selector--in-head' : ''}`}>
            <button
                type="button"
                className={`mesero-add-platillo-btn${floating ? ' mesero-add-platillo-btn--fab' : ''}`}
                onClick={openModal}
                title="Agregar platillo"
                aria-label="Agregar platillo"
            >
                {floating ? (
                    <>
                        <span className="mesero-add-platillo-btn__icon" aria-hidden="true">+</span>
                        <span className="mesero-add-platillo-btn__label">Platillo</span>
                    </>
                ) : (
                    '+ Agregar platillo'
                )}
            </button>

            {modalOpen && createPortal(
                <div
                    className="platillo-modal-overlay"
                    data-touch-scroll-exempt
                    onClick={closeModal}
                >
                    <div
                        className="platillo-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="platillo-modal__header">
                            <span className="platillo-modal__title">Agregar platillos</span>
                            <button
                                type="button"
                                className="platillo-modal__close"
                                onClick={closeModal}
                                title="Cerrar"
                            >
                                ✕
                            </button>
                        </div>
                        <div
                            ref={modalBodyRef}
                            className="platillo-modal__body"
                            data-mesero-platillo-scroll
                        >
                            {platillosByCategory.length === 0 ? (
                                <p className="platillo-modal__empty">No hay platillos disponibles.</p>
                            ) : (
                                platillosByCategory.map((section) => (
                                    <section key={section.name} className="platillo-modal__section">
                                        <h3 className="platillo-modal__category">{section.name}</h3>
                                        <div className="platillo-modal__grid">
                                            {section.items.map((platillo, index) => {
                                                const disabled = platillo.Disponibilidad === 0;
                                                const key = platilloKey(platillo);
                                                const qty = cart[key]?.qty || 0;
                                                const inCart = qty > 0;

                                                return (
                                                    <div
                                                        key={`${section.name}-${platillo.NombrePlatillo}-${index}`}
                                                        className={`platillo-modal__item${disabled ? ' platillo-modal__item--disabled' : ''}${inCart ? ' platillo-modal__item--in-cart' : ''}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="platillo-modal__item-main"
                                                            onClick={() => handlePlatilloTap(platillo)}
                                                            disabled={disabled}
                                                        >
                                                            <img
                                                                src={platillo.Imagen}
                                                                alt={platillo.NombrePlatillo}
                                                            />
                                                            <span>{platillo.NombrePlatillo}</span>
                                                        </button>
                                                        {inCart && (
                                                            <div className="platillo-modal__qty">
                                                                <button
                                                                    type="button"
                                                                    className="platillo-modal__qty-btn"
                                                                    onClick={() => adjustQty(platillo, -1)}
                                                                    aria-label={`Quitar uno de ${platillo.NombrePlatillo}`}
                                                                >
                                                                    −
                                                                </button>
                                                                <span className="platillo-modal__qty-value">{qty}</span>
                                                                <button
                                                                    type="button"
                                                                    className="platillo-modal__qty-btn"
                                                                    onClick={() => adjustQty(platillo, 1)}
                                                                    aria-label={`Agregar uno de ${platillo.NombrePlatillo}`}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                ))
                            )}
                        </div>
                        <div className="platillo-modal__footer">
                            <button
                                type="button"
                                className="platillo-modal__confirm"
                                onClick={handleConfirmAdd}
                                disabled={totalItems === 0}
                            >
                                Agregar{totalItems > 0 ? ` (${totalItems})` : ''}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default MeseroPlatilloSelector;
